import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ActivityAddressModel, ActivityModel } from '../../../../../../core/models/activity.model';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { EstablishmentService } from '../../establishment.service';
import { PublicTypeModel } from '../../../../../../core/models/public-type.model';
import { AddressModel } from '../../../../../../core/models/address.model';
import { AddressService } from '../../../../../../core/services/address.service';
import { ErrorApi } from '../../../../../../core/models/api/error-api';
import { ActivityTypeModel } from '../../../../../../core/models/activity-type.model';
import { ActivityTypeService } from '../../../../settings/activity-type/activity-type.service';
import { map } from 'rxjs/operators';
import { PublicTypeService } from '../../../../settings/public-type/public-type.service';
import { EstablishmentTypeEnum } from '../../../../../../core/enums/establishment-type.enum';
import { BuildingModel } from '../../../../../../core/models/building.model';
import { BuildingService } from '../../../building/building.service';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';
import icClose from '@iconify/icons-ic/twotone-close';
import { AddressUtils } from '../../../../../../core/utils/address-utils';
import { EstablishmentActivityFinessDuplicateWarningComponent } from '../establishment-activity-finess-duplicate-warning/establishment-activity-finess-duplicate-warning.component';

@Component({
  selector: 'vex-establishment-activity-create-update-delete',
  templateUrl: './establishment-activity-create-update-delete.component.html',
  styleUrls: ['./establishment-activity-create-update-delete.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EstablishmentActivityCreateUpdateDeleteComponent implements OnInit, OnDestroy {
  defaults: ActivityModel;
  mode: CrudModeEnum = CrudModeEnum.Create;
  form: UntypedFormGroup;
  loading = false;
  establishmentId: string;
  activityTypeItem: Observable<ActivityTypeModel[]>;
  publicTypeItem: Observable<PublicTypeModel[]>;
  addressItem$: Observable<AddressModel[]>;
  establishmentType: EstablishmentTypeEnum;
  EstablishmentTypeEnum = EstablishmentTypeEnum;
  showFinessFields = false;
  protected subscription = new Subscription();
  selectedBuildings: { [key: number]: BuildingModel } = {};
  candidateBuildings: { [key: number]: BuildingModel[] } = {};
  private surfaceBaseValidators: { [key: number]: any } = {};
  filteredAddressLists: { [key: number]: Observable<AddressModel[]> } = {};
  availableSurfaces: { [key: number]: number } = {};
  private loadingBuildingsForIndex: { [key: number]: boolean } = {};

  // Pour la gestion des dépendances
  readonly icClose = icClose;
  isVerified = false;
  dependencies: any;
  hasDependencies = false;
  /** false si des paniers en cours bloquent la suppression (PRED-3280) */
  canDelete = true;

  /**
   * Validateur personnalisé : au moins 1 des 3 champs de capacité doit être renseigné
   * S'applique uniquement quand les champs FINESS sont affichés (showFinessFields=true)
   */
  private static atLeastOneCapacityFieldValidator(group: UntypedFormGroup): ValidationErrors | null {
    const nbOfPermanantBed = group.get('nbOfPermanantBed')?.value;
    const nbOfTemporaryBed = group.get('nbOfTemporaryBed')?.value;
    const accomodationCapacity = group.get('accomodationCapacity')?.value;

    const hasAtLeastOne =
      (nbOfPermanantBed !== null && nbOfPermanantBed !== undefined && nbOfPermanantBed !== '') ||
      (nbOfTemporaryBed !== null && nbOfTemporaryBed !== undefined && nbOfTemporaryBed !== '') ||
      (accomodationCapacity !== null && accomodationCapacity !== undefined && accomodationCapacity !== '');

    return hasAtLeastOne ? null : { atLeastOneCapacityRequired: true };
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<ActivityModel>,
    private service: EstablishmentService,
    private activityTypeService: ActivityTypeService,
    private publicTypeService: PublicTypeService,
    private addressService: AddressService,
    private buildingService: BuildingService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
  ) {
    if (this.data) {
      this.defaults = this.data?.defaults ?? ({} as ActivityModel);
      this.mode = this.data.mode;
      this.establishmentId = this.data?.establishment?._id;
      this.establishmentType = this.data?.establishment?.establishmentType;
    }
  }

  ngOnInit(): void {
    this.setUpAddressItem();
    this.autocompleteActivityTypeChange();
    this.autocompletePublicTypeChange();
    this.initForm();
    this.initializeAddressSubscriptions();

    // Appeler updateValidatorsByEstablishmentType après l'initialisation du formulaire
    // mais cela ne devrait plus affecter le champ FINESS car il a été retiré de la liste
    this.updateValidatorsByEstablishmentType(this.establishmentType);

    // L'état des champs FINESS est déjà géré dans initForm() via toggleFinessFieldAvailability
    // Pas besoin de le rappeler ici

    // Si on est en mode modification et qu'on a des adresses mais pas les données, les charger
    if (this.isUpdateMode() && this.defaults?.addresses?.length) {
      this.loadAddressesForUpdate();
    }

    // Initialiser les listes filtrées après l'initialisation des souscriptions
    setTimeout(() => {
      this.updateAllAddressLists();
    }, 100);
  }

  initForm() {
    this.form = new UntypedFormGroup({
      _id: new UntypedFormControl(this.defaults?._id),
      label: new UntypedFormControl(this.defaults?.label),
      activityType: new UntypedFormControl(this.defaults?.activityType),
      publicType: new UntypedFormControl(this.defaults?.publicType),
      percent: new UntypedFormControl(this.defaults?.percent),
      finess: new UntypedFormControl({ value: this.defaults?.finess, disabled: true }, [
        Validators.required,
        Validators.maxLength(9),
      ]),
      addresses: this.createAddressFormArray(),
      nbOfPermanantBed: new UntypedFormControl(this.defaults?.nbOfPermanantBed),
      nbOfTemporaryBed: new UntypedFormControl(this.defaults?.nbOfTemporaryBed),
      accomodationCapacity: new UntypedFormControl(this.defaults?.accomodationCapacity),
    });

    // Logique pour activer/désactiver le champ finess selon activityType.isFiness
    this.form.get('activityType')?.valueChanges.subscribe(activityType => {
      this.toggleFinessFieldAvailability(activityType);
    });

    // Appliquer l'état initial si un activityType est déjà sélectionné
    const initialActivityType = this.form.get('activityType')?.value;
    if (initialActivityType) {
      this.toggleFinessFieldAvailability(initialActivityType);
    }

    // Logique d'exclusion mutuelle entre capacité d'accueil et lits (temporairement désactivée)
    // setTimeout(() => {
    //     try {
    //         this.setupMutualExclusionLogic();
    //     } catch (error) {
    //         console.error('Erreur lors de l\'initialisation de la logique d\'exclusion mutuelle:', error);
    //     }
    // }, 100);
  }

  save(): void {
    // Validation spécifique pour les activités FINESS
    if (this.showFinessFields && this.addressControls.length > 1) {
      this.snackbarService.warning("Une activité FINESS ne peut avoir qu'une seule adresse.");
      this.form.markAllAsTouched();
      this.syncCapacityFieldsErrors();
      return;
    }

    // Bloquer tant qu'un bâtiment n'est pas choisi parmi plusieurs candidats
    const pendingIndex = this.addressControls.controls.findIndex(
      (_, i) => (this.candidateBuildings[i]?.length || 0) > 1 && !this.selectedBuildings[i],
    );
    if (pendingIndex !== -1) {
      this.snackbarService.warning(`Veuillez sélectionner un bâtiment pour l'adresse ${pendingIndex + 1}.`);
      this.form.markAllAsTouched();
      return;
    }

    if (this.isCreateMode()) {
      this.createItem();
    } else if (this.isUpdateMode()) {
      this.updateItem();
    }
  }

  /**
   * Enchaîne les confirmations FINESS non bloquantes (R2 V2 puis R3) avant
   * de poursuivre la soumission. Renvoie true si une modale a été ouverte
   * (la suite passera par `onAllConfirmed`), false si rien à confirmer.
   */
  private askFinessDuplicateConfirmation(onAllConfirmed: () => void): boolean {
    const tripletDuplicate = this.findFinessActivityTypeAddressDuplicate();
    if (tripletDuplicate) {
      this.openFinessConfirmation(this.buildTripletDuplicateMessage(tripletDuplicate), () => {
        if (!this.askFinessDifferentAddressConfirmation(onAllConfirmed)) onAllConfirmed();
      });
      return true;
    }
    return this.askFinessDifferentAddressConfirmation(onAllConfirmed);
  }

  private askFinessDifferentAddressConfirmation(onConfirm: () => void): boolean {
    const duplicate = this.findFinessDuplicateOnDifferentAddress();
    if (!duplicate) return false;

    this.openFinessConfirmation(this.buildDifferentAddressMessage(duplicate), onConfirm);
    return true;
  }

  private openFinessConfirmation(message: string, onConfirm: () => void): void {
    const sub = this.dialog
      .open(EstablishmentActivityFinessDuplicateWarningComponent, {
        width: '500px',
        data: { message },
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) onConfirm();
      });
    this.subscription.add(sub);
  }

  /**
   * R2 V2 : retourne une activité du même établissement qui partage le même
   * triplet (numéro FINESS, type d'activité, adresse) que le formulaire courant.
   */
  private findFinessActivityTypeAddressDuplicate(): ActivityModel | null {
    if (!this.showFinessFields) return null;

    const currentFiness = this.form.get('finess')?.value;
    if (currentFiness === null || currentFiness === undefined || currentFiness === '') return null;

    const currentActivityTypeId = this.form.get('activityType')?.value?._id?.toString();
    if (!currentActivityTypeId) return null;

    const currentActivityId = this.form.get('_id')?.value?.toString();
    const currentAddressIds = this.getCurrentAddressIds();
    if (currentAddressIds.length === 0) return null;

    const normalizedFiness = String(currentFiness ?? '').trim();
    const activities: ActivityModel[] = this.data?.establishment?.activities || [];
    return (
      activities.find(
        activity =>
          activity._id?.toString() !== currentActivityId &&
          String(activity.finess ?? '').trim() === normalizedFiness &&
          activity.activityType?._id?.toString() === currentActivityTypeId &&
          (activity.addresses || []).some(addr => currentAddressIds.includes(addr.addressId?.toString())),
      ) || null
    );
  }

  /**
   * R3 : retourne une activité du même établissement qui partage le même
   * numéro FINESS mais aucune adresse en commun avec le formulaire courant.
   * Le cas bloquant (FINESS présent dans un autre établissement) est traité
   * côté back.
   */
  private findFinessDuplicateOnDifferentAddress(): ActivityModel | null {
    if (!this.showFinessFields) return null;

    const currentFiness = this.form.get('finess')?.value;
    if (currentFiness === null || currentFiness === undefined || currentFiness === '') return null;

    const currentActivityId = this.form.get('_id')?.value?.toString();
    const currentAddressIds = this.getCurrentAddressIds();

    const normalizedFiness = String(currentFiness ?? '').trim();
    const activities: ActivityModel[] = this.data?.establishment?.activities || [];
    return (
      activities.find(
        activity =>
          activity._id?.toString() !== currentActivityId &&
          String(activity.finess ?? '').trim() === normalizedFiness &&
          !(activity.addresses || []).some(addr => currentAddressIds.includes(addr.addressId?.toString())),
      ) || null
    );
  }

  private getCurrentAddressIds(): string[] {
    return this.addressControls.controls
      .map(ctrl => ctrl.get('addressId')?.value?._id)
      .filter((id): id is string => !!id)
      .map(id => id.toString());
  }

  private buildTripletDuplicateMessage(duplicate: ActivityModel): string {
    const finess = this.form.get('finess')?.value;
    const activityTypeLabel = duplicate.activityType?.label || 'inconnu';
    const sharedAddressId = (duplicate.addresses || []).find(addr =>
      this.getCurrentAddressIds().includes(addr.addressId?.toString()),
    )?.addressId;
    const addressLabel = this.formatAddressFromForm(sharedAddressId?.toString());
    return `L'activité ${activityTypeLabel} est déjà affectée au Finess ${finess} à l'adresse ${addressLabel}, êtes-vous certain(e) de vouloir continuer ?`;
  }

  private buildDifferentAddressMessage(duplicate: ActivityModel): string {
    const finess = this.form.get('finess')?.value;
    return `Le numéro Finess ${finess} est déjà attribué pour l'activité ${duplicate.label}, êtes-vous certain(e) de vouloir continuer ?`;
  }

  private formatAddressFromForm(addressId?: string): string {
    if (!addressId) return 'inconnue';
    const match = this.addressControls.controls
      .map(ctrl => ctrl.get('addressId')?.value)
      .find(addr => addr?._id?.toString() === addressId);
    return match?.display || match?.label || 'inconnue';
  }

  close(data?: any): void {
    this.dialogRef.close(data);
  }

  isDeleteMode() {
    return this.mode === CrudModeEnum.Delete;
  }

  isCreateMode() {
    return this.mode === CrudModeEnum.Create;
  }

  isUpdateMode() {
    return this.mode === CrudModeEnum.Update;
  }

  isArchiveMode() {
    return this.mode === CrudModeEnum.Archive;
  }

  createItem() {
    if (this.askFinessDuplicateConfirmation(() => this.performCreate())) return;
    this.performCreate();
  }

  private performCreate() {
    this.loading = true;
    this.clearServerMessages();
    this.defaults = this.form.value;
    this.beforeCreateItem();
    if (this.form.valid) {
      const sub = this.service.createActivity(this.establishmentId, this.defaults).subscribe(
        res => {
          this.close(res);
          this.loading = false;
        },
        err => {
          this.setErrorsMessages(err.error.errors);
          this.loading = false;
        },
      );
      this.subscription.add(sub);
    } else {
      this.form.markAllAsTouched();
      this.syncCapacityFieldsErrors();
      this.loading = false;
    }
  }

  updateItem() {
    if (this.askFinessDuplicateConfirmation(() => this.performUpdate())) return;
    this.performUpdate();
  }

  private performUpdate() {
    this.loading = true;
    this.clearServerMessages();
    this.defaults = this.form.value;
    this.beforeUpdateItem();
    if (this.form.valid) {
      const sub = this.service.updateActivity(this.establishmentId, this.defaults).subscribe(
        result => {
          this.close(result);
          this.loading = false;
        },
        error => {
          this.setErrorsMessages(error.error.errors);
          this.loading = false;
        },
      );
      this.subscription.add(sub);
    } else {
      this.form.markAllAsTouched();
      this.syncCapacityFieldsErrors();
      this.loading = false;
    }
  }

  setErrorsMessages(errors: Array<ErrorApi>) {
    errors?.forEach(error => {
      let err = this.form.controls[error.property]?.errors;
      if (err === null) {
        err = { messages: [] };
      }
      err.messages.push(error.message);
      this.form.controls[error.property]?.setErrors(err);
    });
    this.form.markAllAsTouched();
  }

  /**
   * Retire les messages serveur posés par setErrorsMessages, sans toucher aux
   * validations Angular natives (required, min, atLeastOneCapacityRequired…).
   * Sinon un message renvoyé par le back persiste tant que l'utilisateur n'a
   * pas modifié ce champ précis, même s'il a corrigé la cause sur un autre.
   */
  private clearServerMessages(control: AbstractControl = this.form): void {
    if (control instanceof UntypedFormControl) {
      if (control.errors?.messages) {
        const { messages, ...rest } = control.errors;
        control.setErrors(Object.keys(rest).length ? rest : null);
      }
      return;
    }
    if (control instanceof UntypedFormGroup) {
      Object.values(control.controls).forEach(child => this.clearServerMessages(child));
      return;
    }
    if (control instanceof UntypedFormArray) {
      control.controls.forEach(child => this.clearServerMessages(child));
    }
  }

  deleteItem() {
    this.loading = true;
    const sub = this.service.archiveActivity(this.establishmentId, this.defaults?._id).subscribe(
      result => {
        this.close(result);
        this.loading = false;
      },
      error => {
        this.setErrorsMessages(error.error.errors);
        this.loading = false;
      },
    );
    this.subscription.add(sub);
  }

  autocompleteActivityTypeChange(search: string = '') {
    console.log('this.establishmentType');
    this.activityTypeItem = this.activityTypeService
      .findAll(null, null, null, null, search, {
        establishmentType: this.establishmentType,
      })
      .pipe(
        map(x => {
          // Filtrer les activités FINESS si l'établissement est de type SUPPORT_FUNCTION
          let activities = x.data;

          if (this.establishmentType === EstablishmentTypeEnum.SUPPORT_FUNCTION) {
            // Exclure les activités FINESS pour les établissements Fonction support
            activities = activities.filter(activityType => !activityType.isFiness);
          }

          // Ajouter l'acronyme et le label "• FINESS" aux activités
          return activities.map(activityType => {
            const prefix = activityType.acronym ? `${activityType.acronym} - ` : '';
            const suffix = activityType.isFiness ? ' • FINESS' : '';
            return {
              ...activityType,
              label: `${prefix}${activityType.label}${suffix}`,
            };
          });
        }),
      );
  }

  autocompletePublicTypeChange(search: string = '') {
    this.publicTypeItem = this.publicTypeService.findAll(null, null, null, null, search).pipe(map(x => x.data));
  }

  setUpAddressItem() {
    this.addressAutocompleteChange();
  }

  addressAutocompleteChange(state = '') {
    // Utiliser findAll avec le paramètre search pour tous les cas
    this.addressItem$ = this.addressService
      .findAll(1, state?.trim() ? 50 : 100, 'street', 'asc', state?.trim() || '', {
        establishmentId: this.establishmentId,
        isActive: true,
        forActivityCreation: true,
        requiredFields: ['_id', 'label', 'street', 'additionalAddress', 'city', 'postalCode', 'country', 'buildingIds'],
      })
      .pipe(map(x => AddressUtils.withDisplayMany(x.data)));
  }

  toggleFinessFieldAvailability(activityType: ActivityTypeModel) {
    const finessControl = this.form.get('finess');

    if (this.establishmentType !== EstablishmentTypeEnum.CARE) {
      this.showFinessFields = false;
      finessControl?.disable();
      finessControl?.clearValidators();

      // Retirer le validateur si les champs ne sont pas affichés
      this.form.setValidators(null);
    } else {
      // Vérifier si c'est une activité FINESS
      // En mode modification, isFiness peut être undefined, donc on vérifie aussi le label
      const isFiness =
        activityType?.isFiness === true || (activityType?.label && activityType.label.includes('FINESS'));

      this.showFinessFields = isFiness;

      // Activer ou désactiver le champ FINESS selon le type d'activité
      if (this.showFinessFields) {
        finessControl?.enable();

        // Forcer l'application du validateur required sur le &champ FINESS
        finessControl?.setValidators([Validators.required]);
        finessControl?.updateValueAndValidity();

        // Appliquer le validateur personnalisé au niveau du form
        this.form.setValidators(EstablishmentActivityCreateUpdateDeleteComponent.atLeastOneCapacityFieldValidator);

        // Abonnement aux changements des 3 champs de capacité pour synchroniser les erreurs visuelles
        const capacityFields = ['nbOfPermanantBed', 'nbOfTemporaryBed', 'accomodationCapacity'];
        capacityFields.forEach(fieldName => {
          const control = this.form.get(fieldName);
          if (control) {
            const sub = control.valueChanges.subscribe(() => {
              this.syncCapacityFieldsErrors();
            });
            this.subscription.add(sub);

            // Écouter également les événements de changement de statut (touched, etc.)
            const statusSub = control.statusChanges.subscribe(() => {
              this.syncCapacityFieldsErrors();
            });
            this.subscription.add(statusSub);
          }
        });

        // Synchroniser immédiatement les erreurs
        setTimeout(() => this.syncCapacityFieldsErrors(), 0);
      } else {
        finessControl?.disable();
        finessControl?.clearValidators();

        // Retirer le validateur si les champs ne sont pas affichés
        this.form.setValidators(null);
      }
    }

    // Mettre à jour la validité du formulaire
    this.form.updateValueAndValidity();

    // Si on passe en mode FINESS et qu'il y a plus d'une adresse, afficher un avertissement
    if (this.showFinessFields && this.addressControls.length > 1) {
      this.snackbarService.warning(
        "Attention : Les activités FINESS ne peuvent avoir qu'une seule adresse. " +
          "Veuillez supprimer les adresses supplémentaires avant d'enregistrer.",
      );
    }
  }

  updateValidatorsByEstablishmentType(type: EstablishmentTypeEnum) {
    const allFields = [
      'label',
      'activityType',
      'publicType',
      'percent',
      'nbOfPermanantBed',
      'nbOfTemporaryBed',
      'accomodationCapacity',
    ];
    const requiredCARE = ['label', 'activityType', 'publicType', 'percent'];
    const requiredSUPPORT = ['label', 'activityType', 'percent'];

    allFields.forEach(field => {
      const control = this.form.get(field);
      if (!control) {
        return;
      }

      let isRequired = false;

      if (type === EstablishmentTypeEnum.CARE) {
        isRequired = requiredCARE.includes(field);
      }

      if (type === EstablishmentTypeEnum.SUPPORT_FUNCTION) {
        isRequired = requiredSUPPORT.includes(field);
      }

      control.setValidators(isRequired ? [Validators.required] : []);
      control.updateValueAndValidity();
    });

    // Le champ FINESS a son propre validateur Validators.required qui est géré par toggleFinessFieldAvailability
    // Ne pas le réinitialiser ici

    // Gérer la validation des adresses
    this.updateAddressValidators(type);
  }

  get addressControls() {
    return this.form.get('addresses') as UntypedFormArray;
  }

  createAddressFormArray(): UntypedFormArray {
    const addresses = this.defaults?.addresses ? [...this.defaults.addresses] : [];

    if (addresses.length === 0) {
      const buildings = this.data?.establishment?.buildings || [];
      if (buildings.length === 1) {
        const building = buildings[0];
        const mainAddress = building.addresses?.find((a: any) => a.isMain) || building.addresses?.[0];
        if (mainAddress) {
          addresses.push({
            buildingId: building._id,
            addressId: mainAddress._id,
            surface: null,
            addressData: mainAddress,
          } as ActivityAddressModel);
        }
      }
    }

    if (addresses.length === 0) {
      return new UntypedFormArray([this.createAddressFormGroup()]);
    }

    const formGroups = addresses.map(address => this.createAddressFormGroup(address));
    return new UntypedFormArray(formGroups);
  }

  createAddressFormGroup(address?: ActivityAddressModel): UntypedFormGroup {
    const initialAddress = address?.addressData
      ? AddressUtils.withDisplay({ ...address.addressData })
      : address?.addressId;
    return new UntypedFormGroup({
      addressId: new UntypedFormControl(initialAddress, [Validators.required]),
      surface: new UntypedFormControl(address?.surface, [Validators.required, Validators.min(0.1)]),
      buildingId: new UntypedFormControl(address?.buildingId),
    });
  }

  addAddress() {
    // Vérification : empêcher l'ajout si activité FINESS et déjà une adresse
    if (!this.canAddAddress()) {
      this.snackbarService.warning("Les activités FINESS ne peuvent avoir qu'une seule adresse.");
      return;
    }

    const newIndex = this.addressControls.length;
    this.addressControls.push(this.createAddressFormGroup());
    this.updateAddressValidators(this.establishmentType);

    // Initialiser la souscription pour le nouveau contrôle d'adresse
    const newAddressGroup = this.addressControls.at(newIndex) as UntypedFormGroup;
    this.initAddressSubscription(newAddressGroup, newIndex);

    // Mettre à jour les listes d'adresses pour tous les contrôles
    this.updateAllAddressLists();
  }

  removeAddress(index: number) {
    if (this.addressControls.length > 1) {
      // Récupérer l'adresse à supprimer pour identifier le bâtiment concerné
      const addressGroup = this.addressControls.at(index) as UntypedFormGroup;
      const selectedAddress = addressGroup?.get('addressId')?.value;
      const buildingId = selectedAddress?.buildingIds?.[0];

      this.addressControls.removeAt(index);
      delete this.selectedBuildings[index];
      delete this.filteredAddressLists[index];
      delete this.availableSurfaces[index];
      delete this.loadingBuildingsForIndex[index];
      delete this.candidateBuildings[index];

      // Réorganiser les clés des selectedBuildings et filteredAddressLists
      this.reorganizeSelectedBuildings(index);
      this.reorganizeFilteredLists(index);
      this.reorganizeAvailableSurfaces(index);
      this.reorganizeLoadingFlags(index);
      this.reorganizeCandidateBuildings(index);

      // Mettre à jour les surfaces pour le bâtiment concerné
      if (buildingId) {
        this.updateAllSurfacesForBuilding(buildingId);
      }

      // Mettre à jour les listes d'adresses pour tous les contrôles restants
      this.updateAllAddressLists();
    }
  }

  onAddressSelected(address: AddressModel, index: number) {
    if (!address || !address.buildingIds?.length) {
      this.clearAddressSelection(index);
      return;
    }

    // Protection contre les appels multiples pendant le chargement
    if (this.loadingBuildingsForIndex[index]) {
      return;
    }
    this.loadingBuildingsForIndex[index] = true;

    // Récupérer tous les buildings en une seule requête (optimisation N requêtes → 1)
    const sub = this.buildingService.findByIds(address.buildingIds).subscribe({
      next: response => {
        this.loadingBuildingsForIndex[index] = false;
        const buildings: BuildingModel[] = response.data;
        const linkedBuildings = this.filterBuildingsLinkedToEstablishment(buildings);

        if (linkedBuildings.length === 0) {
          this.snackbarService.warning("Aucun bâtiment de cette adresse n'est lié à cet établissement.");
          this.clearAddressSelection(index);
        } else if (linkedBuildings.length === 1) {
          delete this.candidateBuildings[index];
          this.applySelectedBuilding(linkedBuildings[0], index);
        } else {
          this.candidateBuildings[index] = linkedBuildings;

          // Si un bâtiment est déjà mémorisé (mode édition), le pré-sélectionner
          const savedBuildingId = this.addressControls.at(index)?.get('buildingId')?.value;
          const preSelected = savedBuildingId
            ? linkedBuildings.find(b => b._id?.toString() === savedBuildingId?.toString())
            : null;

          if (preSelected) {
            this.applySelectedBuilding(preSelected, index);
          } else {
            delete this.selectedBuildings[index];
            delete this.availableSurfaces[index];
            this.applySurfaceValidator(index);
          }
        }
      },
      error: () => {
        this.loadingBuildingsForIndex[index] = false;
      },
    });
    this.subscription.add(sub);

    // Mettre à jour les listes d'adresses pour les autres contrôles
    this.updateAllAddressLists();
  }

  /**
   * Appelé quand l'utilisateur choisit un bâtiment dans la liste inline.
   * Synchronise le formControl buildingId et pré-remplit la surface avec
   * la totalité de la surface encore disponible sur le nouveau bâtiment.
   */
  onCandidateBuildingSelected(building: BuildingModel, index: number): void {
    if (!building) {
      return;
    }
    this.applySelectedBuilding(building, index);

    const addressGroup = this.addressControls.at(index) as UntypedFormGroup;
    addressGroup.get('buildingId')?.setValue(building._id, { emitEvent: false });

    const usedByOthers = this.calculateTotalUsedSurfaceInBuilding(building._id, index);
    const defaultSurface = Math.max(0, (building.availableSurface ?? 0) - usedByOthers);
    addressGroup.get('surface')?.setValue(defaultSurface);
  }

  /**
   * Surface disponible du bâtiment candidat pour l'établissement courant
   */
  getCandidateBuildingAvailableSurface(building: BuildingModel): string {
    const relation = building.establishments?.find(est => est._id?.toString() === this.establishmentId);
    return (relation?.surface || 0).toFixed(2);
  }

  /**
   * Vérifie si un bâtiment candidat est celui actuellement sélectionné pour l'index
   */
  isCandidateBuildingSelected(building: BuildingModel, index: number): boolean {
    return this.selectedBuildings[index]?._id?.toString() === building?._id?.toString();
  }

  /**
   * Filtre les bâtiments liés à l'établissement courant
   */
  private filterBuildingsLinkedToEstablishment(buildings: BuildingModel[]): BuildingModel[] {
    return buildings.filter(building =>
      building.establishments?.some(est => est._id?.toString() === this.establishmentId),
    );
  }

  /**
   * Applique le bâtiment sélectionné (logique existante extraite)
   */
  private applySelectedBuilding(building: BuildingModel, index: number): void {
    const relation = building.establishments?.find((est: any) => est._id?.toString() === this.establishmentId);
    const allocated = relation?.surface || 0;
    const occupied = (this.data.establishment?.activities || [])
      .filter((act: any) => act._id !== this.form.get('_id')?.value)
      .flatMap((act: any) => act.addresses || [])
      .filter((addr: any) => addr.buildingId === building._id)
      .reduce((sum: number, addr: any) => sum + (addr.surface || 0), 0);

    building.availableSurface = allocated - occupied;
    this.selectedBuildings[index] = building;
    this.applySurfaceValidator(index);
    this.updateAllSurfacesForBuilding(building._id);
    this.updateAllAddressLists();
  }

  /**
   * Efface la sélection d'adresse
   */
  private clearAddressSelection(index: number): void {
    const addressGroup = this.addressControls.at(index) as UntypedFormGroup;
    addressGroup?.get('addressId')?.setValue(null, { emitEvent: false });
    delete this.selectedBuildings[index];
    delete this.availableSurfaces[index];
    delete this.candidateBuildings[index];
    this.applySurfaceValidator(index);
    this.updateAllAvailableSurfaces();
    this.updateAllAddressLists();
  }

  private initializeAddressSubscriptions() {
    this.addressControls.controls.forEach((addressGroup, index) => {
      this.initAddressSubscription(addressGroup as UntypedFormGroup, index);
    });
  }

  private initAddressSubscription(addressGroup: UntypedFormGroup, index: number) {
    const addressControl = addressGroup.get('addressId');
    const surfaceControl = addressGroup.get('surface');

    if (addressControl) {
      const sub = addressControl.valueChanges.subscribe((address: AddressModel) => {
        this.onAddressSelected(address, index);
      });
      this.subscription.add(sub);

      // Initialiser si une adresse est déjà sélectionnée
      const initialAddress = addressControl.value;
      if (initialAddress) {
        this.onAddressSelected(initialAddress, index);
      }
    }

    // Souscription aux changements de surface pour mettre à jour l'affichage dynamique
    if (surfaceControl) {
      const surfaceSub = surfaceControl.valueChanges.subscribe(() => {
        // Privilégier l'_id du bâtiment sélectionné (l'adresse peut être
        // liée à plusieurs bâtiments de l'établissement)
        const buildingId = this.selectedBuildings[index]?._id;
        if (buildingId) {
          setTimeout(() => {
            this.updateAllSurfacesForBuilding(buildingId);
          }, 0);
        } else {
          setTimeout(() => {
            this.updateAvailableSurface(index);
          }, 0);
        }
      });
      this.subscription.add(surfaceSub);
    }
  }

  private applySurfaceValidator(index: number) {
    const addressGroup = this.addressControls.at(index) as UntypedFormGroup;
    const surfaceControl = addressGroup.get('surface');
    const addressControl = addressGroup.get('addressId');

    if (!surfaceControl) {
      return;
    }

    const validators = [Validators.required, Validators.min(0.1)];
    const availableSurface = this.selectedBuildings[index]?.availableSurface;
    if (availableSurface !== undefined && availableSurface > 0) {
      validators.push(Validators.max(availableSurface));
    }

    surfaceControl.setValidators(validators);
    surfaceControl.updateValueAndValidity();
  }

  private updateAddressValidators(type: EstablishmentTypeEnum) {
    this.addressControls.controls.forEach((addressGroup, index) => {
      const addressControl = addressGroup.get('addressId');
      const surfaceControl = addressGroup.get('surface');

      if (addressControl) {
        addressControl.setValidators([Validators.required]);
        addressControl.updateValueAndValidity();
      }

      if (surfaceControl) {
        const validators = [Validators.required, Validators.min(0.1)];
        if (type === EstablishmentTypeEnum.SUPPORT_FUNCTION) {
          // Pour les fonctions support, la surface est obligatoire
          surfaceControl.setValidators(validators);
        } else {
          // Pour les soins, la surface peut être optionnelle selon les besoins
          surfaceControl.setValidators([Validators.min(0.1)]);
        }
        surfaceControl.updateValueAndValidity();
      }
    });
  }

  private reorganizeSelectedBuildings(removedIndex: number) {
    const newSelectedBuildings: { [key: number]: BuildingModel } = {};
    Object.keys(this.selectedBuildings).forEach(key => {
      const index = parseInt(key);
      if (index > removedIndex) {
        newSelectedBuildings[index - 1] = this.selectedBuildings[index];
      } else if (index < removedIndex) {
        newSelectedBuildings[index] = this.selectedBuildings[index];
      }
    });
    this.selectedBuildings = newSelectedBuildings;
  }

  private reorganizeFilteredLists(removedIndex: number) {
    const newFilteredLists: { [key: number]: Observable<AddressModel[]> } = {};
    Object.keys(this.filteredAddressLists).forEach(key => {
      const index = parseInt(key, 10);
      if (index > removedIndex) {
        newFilteredLists[index - 1] = this.filteredAddressLists[index];
      } else if (index < removedIndex) {
        newFilteredLists[index] = this.filteredAddressLists[index];
      }
    });
    this.filteredAddressLists = newFilteredLists;
  }

  private reorganizeAvailableSurfaces(removedIndex: number) {
    const newAvailableSurfaces: { [key: number]: number } = {};
    Object.keys(this.availableSurfaces).forEach(key => {
      const index = parseInt(key, 10);
      if (index > removedIndex) {
        newAvailableSurfaces[index - 1] = this.availableSurfaces[index];
      } else if (index < removedIndex) {
        newAvailableSurfaces[index] = this.availableSurfaces[index];
      }
    });
    this.availableSurfaces = newAvailableSurfaces;
  }

  private reorganizeLoadingFlags(removedIndex: number) {
    const newLoadingFlags: { [key: number]: boolean } = {};
    Object.keys(this.loadingBuildingsForIndex).forEach(key => {
      const index = parseInt(key, 10);
      if (index > removedIndex) {
        newLoadingFlags[index - 1] = this.loadingBuildingsForIndex[index];
      } else if (index < removedIndex) {
        newLoadingFlags[index] = this.loadingBuildingsForIndex[index];
      }
    });
    this.loadingBuildingsForIndex = newLoadingFlags;
  }

  private reorganizeCandidateBuildings(removedIndex: number) {
    const newCandidates: { [key: number]: BuildingModel[] } = {};
    Object.keys(this.candidateBuildings).forEach(key => {
      const index = parseInt(key, 10);
      if (index > removedIndex) {
        newCandidates[index - 1] = this.candidateBuildings[index];
      } else if (index < removedIndex) {
        newCandidates[index] = this.candidateBuildings[index];
      }
    });
    this.candidateBuildings = newCandidates;
  }

  /**
   * Met à jour la surface disponible affichée dynamiquement
   */
  private updateAvailableSurface(index: number) {
    const selectedBuilding = this.selectedBuildings[index];
    if (!selectedBuilding || selectedBuilding.availableSurface === undefined) {
      delete this.availableSurfaces[index];
      return;
    }

    const addressGroup = this.addressControls.at(index) as UntypedFormGroup;
    const selectedAddress = addressGroup?.get('addressId')?.value;

    if (!selectedAddress || !selectedAddress.buildingIds?.length) {
      delete this.availableSurfaces[index];
      return;
    }

    const buildingId = selectedBuilding._id || selectedAddress.buildingIds[0];
    const currentSurface = addressGroup?.get('surface')?.value || 0;

    // Calculer la surface totale utilisée par toutes les autres adresses du même bâtiment
    const totalUsedByOthers = this.calculateTotalUsedSurfaceInBuilding(buildingId, index);

    // Calculer la surface disponible restante (surface totale - surface utilisée par les autres - surface courante)
    const remainingSurface = selectedBuilding.availableSurface - totalUsedByOthers - currentSurface;
    this.availableSurfaces[index] = Math.max(0, remainingSurface);
  }

  /**
   * Calcule la surface totale utilisée dans un bâtiment par les autres adresses (excluant l'index spécifié)
   */
  private calculateTotalUsedSurfaceInBuilding(buildingId: string, excludeIndex?: number): number {
    let totalUsed = 0;

    this.addressControls.controls.forEach((addressGroup, index) => {
      // Ignorer l'index en cours de modification si spécifié
      if (excludeIndex !== undefined && index === excludeIndex) {
        return;
      }

      const selectedAddress = addressGroup.get('addressId')?.value;
      const surface = addressGroup.get('surface')?.value || 0;

      // Vérifier si cette adresse appartient au même bâtiment
      if (selectedAddress && selectedAddress.buildingIds?.includes(buildingId) && surface > 0) {
        totalUsed += surface;
      }
    });

    return totalUsed;
  }

  /**
   * Met à jour toutes les surfaces disponibles pour un bâtiment donné
   */
  private updateAllSurfacesForBuilding(buildingId: string) {
    this.addressControls.controls.forEach((addressGroup, index) => {
      const selectedAddress = addressGroup.get('addressId')?.value;
      if (selectedAddress && selectedAddress.buildingIds?.includes(buildingId)) {
        this.updateAvailableSurface(index);
      }
    });
  }

  /**
   * Met à jour toutes les surfaces disponibles
   */
  private updateAllAvailableSurfaces() {
    this.addressControls.controls.forEach((_, index) => {
      this.updateAvailableSurface(index);
    });
  }

  /**
   * Obtient la surface disponible pour un index donné avec formatage 2 décimales
   */
  getAvailableSurface(index: number): string | undefined {
    const surface = this.availableSurfaces[index];
    return surface !== undefined ? surface.toFixed(2) : undefined;
  }

  /**
   * Obtient la surface disponible brute (nombre) pour un index donné
   */
  getAvailableSurfaceRaw(index: number): number | undefined {
    return this.availableSurfaces[index];
  }

  /**
   * Vérifie si on doit afficher les informations de surface pour un index donné
   */
  shouldShowSurfaceInfo(index: number): boolean {
    return this.selectedBuildings[index] && this.selectedBuildings[index].availableSurface !== undefined;
  }

  /**
   * Vérifie si l'utilisateur peut ajouter une nouvelle adresse
   * Pour les activités FINESS : une seule adresse maximum
   */
  canAddAddress(): boolean {
    if (this.showFinessFields) {
      return this.addressControls.length < 1;
    }
    return true; // Pas de limitation pour les activités non-FINESS
  }

  /**
   * Retourne le message explicatif pour la limitation des adresses
   */
  getAddressLimitMessage(): string {
    if (this.showFinessFields && this.addressControls.length >= 1) {
      return "Les activités FINESS ne peuvent avoir qu'une seule adresse.";
    }
    return '';
  }

  /**
   * Met à jour toutes les listes d'adresses filtrées
   */
  private updateAllAddressLists() {
    const selectedAddressIds = this.getSelectedAddressIds();

    this.addressControls.controls.forEach((_, index) => {
      this.updateAddressListForIndex(index, selectedAddressIds);
    });
  }

  /**
   * Récupère tous les IDs des adresses sélectionnées
   */
  private getSelectedAddressIds(): string[] {
    const ids: string[] = [];
    this.addressControls.controls.forEach(control => {
      const selectedAddress = control.get('addressId')?.value;
      if (selectedAddress && selectedAddress._id) {
        ids.push(selectedAddress._id);
      }
    });
    return ids;
  }

  /**
   * Met à jour la liste filtrée pour un index spécifique
   */
  private updateAddressListForIndex(index: number, allSelectedIds: string[]) {
    const currentControl = this.addressControls.at(index);
    const currentAddressId = currentControl?.get('addressId')?.value?._id;

    // Exclure toutes les autres adresses sélectionnées
    const excludeIds = allSelectedIds.filter(id => id !== currentAddressId);

    this.filteredAddressLists[index] = this.addressService
      .findAll(1, 100, 'street', 'asc', '', {
        establishmentId: this.establishmentId,
        isActive: true,
        forActivityCreation: true,
        requiredFields: ['_id', 'label', 'street', 'additionalAddress', 'city', 'postalCode', 'country', 'buildingIds'],
      })
      .pipe(map(x => AddressUtils.withDisplayMany(x.data.filter(address => !excludeIds.includes(address._id)))));
  }

  /**
   * Obtient la liste filtrée pour un index donné
   */
  getFilteredAddressesForIndex(index: number): Observable<AddressModel[]> {
    if (!this.filteredAddressLists[index]) {
      this.updateAddressListForIndex(index, this.getSelectedAddressIds());
    }
    return this.filteredAddressLists[index] || this.addressItem$;
  }

  /**
   * Gère la recherche dans les adresses pour un index spécifique
   */
  onAddressSearch(searchTerm: string, index: number) {
    const selectedAddressIds = this.getSelectedAddressIds();
    const currentAddressId = this.addressControls.at(index)?.get('addressId')?.value?._id;
    const excludeIds = selectedAddressIds.filter(id => id !== currentAddressId);

    this.filteredAddressLists[index] = this.addressService
      .findAll(1, searchTerm?.trim() ? 50 : 100, 'street', 'asc', searchTerm?.trim() || '', {
        establishmentId: this.establishmentId,
        isActive: true,
        forActivityCreation: true,
        requiredFields: ['_id', 'label', 'street', 'additionalAddress', 'city', 'postalCode', 'country', 'buildingIds'],
      })
      .pipe(map(x => AddressUtils.withDisplayMany(x.data.filter(address => !excludeIds.includes(address._id)))));
  }

  loadAddressesForUpdate() {
    // Charger les adresses complètes si on a seulement les addressId et pas d'addressData
    const addresses = this.defaults?.addresses || [];
    addresses.forEach((address, index) => {
      if (address?.addressId && !address?.addressData) {
        const addressId = address.addressId;
        const sub = this.addressService.findById(addressId).subscribe(
          response => {
            if (response && response.data) {
              const addressGroup = this.addressControls.at(index) as UntypedFormGroup;
              if (addressGroup) {
                const enriched = AddressUtils.withDisplay(response.data);
                addressGroup.get('addressId')?.setValue(enriched);
                if (this.defaults.addresses && this.defaults.addresses[index]) {
                  this.defaults.addresses[index].addressData = enriched;
                }
              }
            }
          },
          error => {
            console.error("Erreur lors du chargement de l'adresse:", error);
          },
        );
        this.subscription.add(sub);
      }
    });
  }

  beforeCreateItem() {
    this.defaults.addresses = this.transformAddressesFromForm();
  }

  beforeUpdateItem() {
    this.defaults.addresses = this.transformAddressesFromForm();
  }

  private transformAddressesFromForm(): ActivityAddressModel[] {
    const addressFormArray = this.form.get('addresses') as UntypedFormArray;
    const addresses: ActivityAddressModel[] = [];

    addressFormArray.controls.forEach((addressGroup: UntypedFormGroup, index: number) => {
      const selectedAddress = addressGroup.get('addressId')?.value;
      const surface = addressGroup.get('surface')?.value;

      if (selectedAddress && selectedAddress._id && surface) {
        // Utiliser le buildingId du bâtiment sélectionné, sinon fallback sur le premier
        const buildingId = this.selectedBuildings[index]?._id || selectedAddress.buildingIds?.[0];

        const addressObj: ActivityAddressModel = {
          buildingId: buildingId,
          addressId: selectedAddress._id,
          surface,
          addressData: selectedAddress,
        };
        addresses.push(addressObj);
      }
    });

    return addresses;
  }

  /**
   * Configure la logique d'exclusion mutuelle entre capacité d'accueil et lits
   */
  private setupMutualExclusionLogic() {
    // Vérifier que le form existe
    if (!this.form) {
      return;
    }

    const accommodationCapacityControl = this.form.get('accomodationCapacity');
    const nbOfPermanantBedControl = this.form.get('nbOfPermanantBed');
    const nbOfTemporaryBedControl = this.form.get('nbOfTemporaryBed');

    if (!accommodationCapacityControl || !nbOfPermanantBedControl || !nbOfTemporaryBedControl) {
      console.warn("FormControls pour la logique d'exclusion mutuelle non trouvés");
      return;
    }

    // Quand la capacité d'accueil change
    const accommodationSub = accommodationCapacityControl.valueChanges.subscribe(value => {
      if (value && value > 0) {
        // Si capacité d'accueil remplie, vider et désactiver les lits
        nbOfPermanantBedControl.setValue(null);
        nbOfTemporaryBedControl.setValue(null);
        nbOfPermanantBedControl.disable();
        nbOfTemporaryBedControl.disable();
      } else {
        // Si capacité d'accueil vide, réactiver les lits
        nbOfPermanantBedControl.enable();
        nbOfTemporaryBedControl.enable();
      }
    });

    // Quand le nombre de lits permanents change
    const permanantBedSub = nbOfPermanantBedControl.valueChanges.subscribe(value => {
      if (value && value > 0) {
        // Si lits permanents remplis, vider et désactiver la capacité d'accueil
        accommodationCapacityControl.setValue(null);
        accommodationCapacityControl.disable();
      } else {
        // Si lits permanents vide et lits temporaires vide aussi, réactiver capacité
        const tempBedValue = nbOfTemporaryBedControl.value;
        if (!tempBedValue || tempBedValue <= 0) {
          accommodationCapacityControl.enable();
        }
      }
    });

    // Quand le nombre de lits temporaires change
    const temporaryBedSub = nbOfTemporaryBedControl.valueChanges.subscribe(value => {
      if (value && value > 0) {
        // Si lits temporaires remplis, vider et désactiver la capacité d'accueil
        accommodationCapacityControl.setValue(null);
        accommodationCapacityControl.disable();
      } else {
        // Si lits temporaires vide et lits permanents vide aussi, réactiver capacité
        const permBedValue = nbOfPermanantBedControl.value;
        if (!permBedValue || permBedValue <= 0) {
          accommodationCapacityControl.enable();
        }
      }
    });

    this.subscription.add(accommodationSub);
    this.subscription.add(permanantBedSub);
    this.subscription.add(temporaryBedSub);

    // Appliquer la logique initiale selon les valeurs existantes
    this.applyInitialMutualExclusionState();
  }

  /**
   * Applique l'état initial de l'exclusion mutuelle selon les valeurs existantes
   */
  private applyInitialMutualExclusionState() {
    const accommodationCapacityControl = this.form.get('accomodationCapacity');
    const nbOfPermanantBedControl = this.form.get('nbOfPermanantBed');
    const nbOfTemporaryBedControl = this.form.get('nbOfTemporaryBed');

    if (!accommodationCapacityControl || !nbOfPermanantBedControl || !nbOfTemporaryBedControl) {
      return;
    }

    const accommodationValue = accommodationCapacityControl.value;
    const permanantBedValue = nbOfPermanantBedControl.value;
    const temporaryBedValue = nbOfTemporaryBedControl.value;

    if (accommodationValue && accommodationValue > 0) {
      // Si capacité d'accueil est remplie, désactiver les lits
      nbOfPermanantBedControl.disable();
      nbOfTemporaryBedControl.disable();
    } else if ((permanantBedValue && permanantBedValue > 0) || (temporaryBedValue && temporaryBedValue > 0)) {
      // Si un des lits est rempli, désactiver la capacité d'accueil
      accommodationCapacityControl.disable();
    }
  }

  /**
   * Synchronise les erreurs visuelles sur les 3 champs de capacité
   * Marque les champs en erreur si aucun n'est rempli ET au moins un est touché
   */
  private syncCapacityFieldsErrors(): void {
    if (!this.showFinessFields) {
      return;
    }

    const nbOfPermanantBed = this.form.get('nbOfPermanantBed');
    const nbOfTemporaryBed = this.form.get('nbOfTemporaryBed');
    const accomodationCapacity = this.form.get('accomodationCapacity');

    const hasValue =
      (nbOfPermanantBed?.value !== null && nbOfPermanantBed?.value !== '' && nbOfPermanantBed?.value !== undefined) ||
      (nbOfTemporaryBed?.value !== null && nbOfTemporaryBed?.value !== '' && nbOfTemporaryBed?.value !== undefined) ||
      (accomodationCapacity?.value !== null &&
        accomodationCapacity?.value !== '' &&
        accomodationCapacity?.value !== undefined);

    const anyTouched = nbOfPermanantBed?.touched || nbOfTemporaryBed?.touched || accomodationCapacity?.touched;

    // Si aucun champ n'est rempli ET au moins un est touché
    if (!hasValue && anyTouched) {
      [nbOfPermanantBed, nbOfTemporaryBed, accomodationCapacity].forEach(control => {
        if (control) {
          const currentErrors = control.errors || {};
          control.setErrors({ ...currentErrors, atLeastOneCapacityRequired: true }, { emitEvent: false });
        }
      });
    } else {
      // Retirer l'erreur si au moins un champ est rempli
      [nbOfPermanantBed, nbOfTemporaryBed, accomodationCapacity].forEach(control => {
        if (control && control.hasError('atLeastOneCapacityRequired')) {
          const currentErrors = { ...control.errors };
          delete currentErrors.atLeastOneCapacityRequired;
          control.setErrors(Object.keys(currentErrors).length > 0 ? currentErrors : null, { emitEvent: false });
        }
      });
    }
  }

  checkDependencies() {
    this.loading = true;
    this.hasDependencies = false;

    if (!this.establishmentId || !this.defaults?._id) {
      this.snackbarService.danger('Données manquantes pour vérifier les dépendances');
      this.loading = false;
      return;
    }

    this.subscription.add(
      this.service.checkActivityDependencies(this.establishmentId, this.defaults._id).subscribe(
        response => {
          console.log('Dependencies response:', response);
          this.dependencies = response.data;
          this.isVerified = true;
          this.hasDependencies = response.data?.hasDependencies || false;
          this.canDelete = response.data?.canChangeDependencies !== false;
          this.loading = false;
        },
        error => {
          console.error('Error checking dependencies:', error);
          this.snackbarService.danger('Erreur lors de la vérification des dépendances');
          this.loading = false;
        },
      ),
    );
  }

  confirmDelete() {
    this.loading = true;
    const sub = this.service.archiveActivity(this.establishmentId, this.defaults._id).subscribe(
      result => {
        this.snackbarService.success("L'activité a été archivée avec succès");
        this.close(result);
        this.loading = false;
      },
      error => {
        console.error('Error archiving activity:', error);
        this.snackbarService.danger(error.error?.message || "Erreur lors de l'archivage de l'activité");
        this.setErrorsMessages(error.error?.errors);
        this.loading = false;
      },
    );
    this.subscription.add(sub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
